import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { SpecialistAssignmentService } from '@/lib/services/specialist-assignment-service'
import type { AssignedCaregiver, SpecialistWithAssignments } from '@/types/specialist-assignment'

interface UnassignCaregiverDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caregiver: AssignedCaregiver
  specialist: SpecialistWithAssignments
  onSuccess: () => void
}

export function UnassignCaregiverDialog({
  open,
  onOpenChange,
  caregiver,
  specialist,
  onSuccess
}: UnassignCaregiverDialogProps) {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const handleUnassign = async () => {
    if (!reason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please provide a reason for unassigning'
      })
      return
    }

    setLoading(true)
    try {
      const result = await SpecialistAssignmentService.unassignCaregiver({
        caregiver_id: caregiver.id,
        specialist_id: specialist.specialist_id,
        reason: reason.trim()
      })

      if (result.success) {
        toast({
          title: 'Unassigned successfully',
          description: `${caregiver.name} has been unassigned from ${specialist.specialist_name}`
        })
        onSuccess()
        onOpenChange(false)
        setReason('')
      } else {
        toast({
          variant: 'destructive',
          title: 'Unassign failed',
          description: result.error || 'An error occurred'
        })
      }
    } catch (error) {
      console.error('Unassign error:', error)
      toast({
        variant: 'destructive',
        title: 'Unassign failed',
        description: 'An unexpected error occurred'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Unassign Caregiver</DialogTitle>
          <DialogDescription>
            Remove {caregiver.name} from {specialist.specialist_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This caregiver will no longer have an assigned specialist. They will not be able to access
              specialist features until reassigned.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (required)</Label>
            <Textarea
              id="reason"
              placeholder="Why is this caregiver being unassigned?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleUnassign}
            disabled={loading || !reason.trim()}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Unassign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
